import React from 'react';

import { Button } from '@/components/shared/element/buttons/button';
import { Link } from '@/components/shared/element/link';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Grid } from '@/components/shared/layout/grid';
import { Image } from '@/components/shared/media/image';
import { CSS, css, styled } from '@/stitches.config';
import { IMenu } from '@/types/layout/menu';

const SubmenuModelLinkStyle = css({
  display: 'grid',
  gap: '$space-4',
  alignContent: 'space-between',
  gridTemplateColumns: 'minmax(auto, 208px)',
  '@lg': {
    gridTemplateColumns: 'minmax(auto, 180px)',
  },
  '@xl': {
    gridTemplateColumns: 'minmax(auto, 208px)',
  },
});

const SubmenuModelLinkImage = styled(Box, {
  variants: {
    maxWidth: {
      true: {
        width: '100%',
        maxWidth: 255,
        mx: 'auto',
      },
    },
  },
});

interface SubmenuModelLinkProps extends IMenu {
  forBrochure?: boolean;
  new?: boolean;
  dataTrack: string;
  dataTest: string;
  transparentCss?: CSS;
}

const SubmenuModelLink = ({
  forBrochure,
  dataTrack,
  dataTest,
  transparentCss,
  ...rest
}: SubmenuModelLinkProps) => {
  return (
    <Link
      className={SubmenuModelLinkStyle()}
      href={rest.url}
      target={rest.target}
      tracking={{
        dataTrack: dataTrack,
        dataTrackText: rest.label,
        dataTrackUrl: rest.url,
      }}
      test={{
        dataTest: dataTest,
      }}
      hoverColor={!forBrochure ? 'red' : undefined}
      {...rest}
      css={
        transparentCss
          ? {
              ...transparentCss,
              '@lg': {
                opacity: 'var(--submenu-opacity)',
                transform: 'var(--submenu-transform)',
                transitionProperty: 'var(--submenu-transition-property)',
                transitionDelay: 'var(--submenu-transition-delay)',
                transitionDuration: 'var(--submenu-transition-duration)',
                transitionTimingFunction: 'var(--submenu-transition-easing)',
                willChange: 'var(--submenu-will-change)',
              },
            }
          : undefined
      }
    >
      <Grid rowGap="1">
        {!!rest.label && (
          <Text size="text-body-1" font="bold" data-test="title">
            {rest.label}
          </Text>
        )}
        {!!rest.description && (
          <Text
            size="text-button-2"
            variant="text-gray-dark"
            data-test="description"
          >
            {rest.description}
          </Text>
        )}
      </Grid>
      {!!rest.image && !!rest.image.src && (
        <SubmenuModelLinkImage position="relative" maxWidth={forBrochure}>
          <Image
            priority={rest.image.priority}
            src={rest.image.src}
            alt={rest.image.alt}
            width={rest.image.width}
            height={rest.image.height}
            layout="responsive"
            aspectRatio="4by3"
            sources={[
              {
                srcset: rest.image.src,
                media: { minWidth: 768 },
                options: { width: 256, crop: '4:3' },
              },
              {
                srcset: rest.image.src,
                media: { maxWidth: 767 },
                options: { width: 185, crop: '4:3' },
              },
            ]}
          />
        </SubmenuModelLinkImage>
      )}

      {!!forBrochure && (
        <Button
          label="ດາວໂຫຼດ"
          icon="file-download"
          border
          variant="button-gray"
          size="md"
          css={{ justifySelf: 'center' }}
          tracking={{
            dataTrack: dataTrack,
            dataTrackText: 'ดาวน์โหลด',
            dataTrackUrl: rest.url,
            dataTrackValue: rest.label,
          }}
        />
      )}
    </Link>
  );
};

SubmenuModelLink.displayName = 'SubmenuModelLink';

export default SubmenuModelLink;
